import type { AnswerSelectDto } from '@@types/api.types';
import { useInnovatorPatchAnswer } from '@queries/answer.query';
import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_DELAY_MS = 300;

type UseAutoSaveAnswerParams = {
  answer?: AnswerSelectDto;
  disableInputs: boolean;
  questionId: string;
  sectionId?: string;
  surveyId?: string;
};

type UseAutoSaveAnswerReturn = {
  details: string;
  flush: () => Promise<string | undefined>;
  isPending: boolean;
  onDetailsBlur: () => Promise<void>;
  onDetailsChange: (value: string) => void;
  onOptionChange: (option: string) => void;
  selectedOption: string | null;
  skipToQuestionId: string | undefined;
};

/**
 * Custom hook that manages auto-save behavior for survey question answers.
 * Handles debounced saving on option change and save-on-blur for details.
 */
export function useAutoSaveAnswer(params: UseAutoSaveAnswerParams): UseAutoSaveAnswerReturn {
  const { answer, disableInputs, questionId, sectionId, surveyId } = params;

  const [selectedOption, setSelectedOption] = useState<string | null>(answer?.response || null);
  const [details, setDetails] = useState<string>(answer?.details || '');
  const [skipToQuestionId, setSkipToQuestionId] = useState<string | undefined>();
  const [pendingAnswer, setPendingAnswer] = useState<Promise<AnswerSelectDto> | null>(null);

  const pendingAnswerRef = useRef(pendingAnswer);
  const skipToQuestionIdRef = useRef(skipToQuestionId);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOptionRef = useRef<string | null>(null);
  const detailsRef = useRef(details);

  const { mutateAsync: patchAnswer } = useInnovatorPatchAnswer();

  // Sync state from answer prop when it changes
  useEffect(() => {
    if (answer?.response) setSelectedOption(answer.response);
    if (answer?.details) setDetails(answer.details);
  }, [answer]);

  // Keep refs in sync with state
  useEffect(() => {
    pendingAnswerRef.current = pendingAnswer;
  }, [pendingAnswer]);

  useEffect(() => {
    skipToQuestionIdRef.current = skipToQuestionId;
  }, [skipToQuestionId]);

  useEffect(() => {
    detailsRef.current = details;
  }, [details]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const saveAnswer = useCallback(
    async (option: string): Promise<AnswerSelectDto> => {
      const answerPromise = patchAnswer({
        details: detailsRef.current,
        questionId,
        response: option,
        sectionId: sectionId!,
        surveyId: surveyId!,
      });
      setPendingAnswer(answerPromise);
      try {
        const patchedAnswer = await answerPromise;
        setSkipToQuestionId(patchedAnswer.skipToQuestionId);
        return patchedAnswer;
      } finally {
        setPendingAnswer(null);
      }
    },
    [patchAnswer, questionId, sectionId, surveyId],
  );

  const onOptionChange = useCallback(
    (option: string) => {
      if (disableInputs) return;

      setSelectedOption(option);
      latestOptionRef.current = option;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        if (sectionId && surveyId && latestOptionRef.current) {
          try {
            await saveAnswer(latestOptionRef.current);
          } catch (error) {
            // Error handling managed by mutation hook or parent
            console.error('Auto-save failed:', error);
          }
        }
      }, DEBOUNCE_DELAY_MS);
    },
    [disableInputs, saveAnswer, sectionId, surveyId],
  );

  const onDetailsChange = useCallback((value: string) => {
    setDetails(value);
  }, []);

  const onDetailsBlur = useCallback(async () => {
    if (disableInputs || !sectionId || !surveyId || !selectedOption) return;

    const answerPromise = patchAnswer({
      details,
      questionId,
      response: selectedOption,
      sectionId,
      surveyId,
    });
    setPendingAnswer(answerPromise);
    try {
      const patchedAnswer = await answerPromise;
      setSkipToQuestionId(patchedAnswer.skipToQuestionId);
    } finally {
      setPendingAnswer(null);
    }
  }, [details, disableInputs, patchAnswer, questionId, sectionId, selectedOption, surveyId]);

  /**
   * Flushes any pending debounced save and waits for completion.
   * Returns the current skipToQuestionId after all saves complete.
   * Used before navigation to ensure data is persisted.
   */
  const flush = useCallback(async (): Promise<string | undefined> => {
    if (disableInputs) return skipToQuestionIdRef.current;

    // Cancel pending debounce and fire immediately if there's a pending option
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;

      if (sectionId && surveyId && latestOptionRef.current) {
        const patchedAnswer = await saveAnswer(latestOptionRef.current);
        return patchedAnswer.skipToQuestionId;
      }
    }

    // Wait for any in-flight request
    if (pendingAnswerRef.current) {
      const patchedAnswer = await pendingAnswerRef.current;
      setPendingAnswer(null);
      return patchedAnswer.skipToQuestionId;
    }

    return skipToQuestionIdRef.current;
  }, [disableInputs, saveAnswer, sectionId, surveyId]);

  return {
    details,
    flush,
    isPending: pendingAnswer !== null,
    onDetailsBlur,
    onDetailsChange,
    onOptionChange,
    selectedOption,
    skipToQuestionId,
  };
}
